import { isBooking } from './bookingsTypeHelper';
import { isConfirmed } from './bookingsActionHelper';

export const getBookingsBaseUrl = (accountKind?: string): string => {
  if (accountKind === 'SITTER') {
    return '/provider/bookings';
  }
  return '/bookings';
};

export const getBookingsUrlWithNotification = (notification: string, accountKind?: string): string => {
  const baseUrl = getBookingsBaseUrl(accountKind);
  return `${baseUrl}?notification=${encodeURIComponent(notification)}`;
};

/**
 * Generates a URL for a contract (booking or interview) or interview request.
 *
 * Note: Contracts can only be 'accepted' or 'confirmed'. However, this function also accepts
 * 'pending' for interview requests (which are not contracts yet) to support URL generation
 * for the interview flow. Once an interview is accepted, it becomes a contract.
 *
 * @param nodeId - The contract UUID (or interview request UUID for pending interviews)
 * @param accountKind - 'SITTER' for provider, 'PARENT' for seeker, or undefined for default (parent)
 * @param contractType - 'booking' or 'interview'
 * @param status - Status: 'accepted' | 'confirmed' | 'pending'
 *   - For contracts: 'accepted' | 'confirmed'
 *   - For interview requests: 'pending' (not a contract)
 * @returns The generated URL path
 */
export const getContractUrl = (
  nodeId: string,
  accountKind?: string,
  contractType?: 'booking' | 'interview',
  status?: 'accepted' | 'confirmed' | 'pending'
): string => {
  const baseUrl = getBookingsBaseUrl(accountKind);
  if (contractType) {
    if (status) {
      return `${baseUrl}/${contractType}/${status}/${nodeId}`;
    }
    return `${baseUrl}/${contractType}/${nodeId}`;
  }
  return `${baseUrl}/${nodeId}`;
};

/**
 * Generates a URL for a booking contract.
 *
 * Booking status is determined by `scCareSession.careGiverValidationStatus`:
 * - 'accepted': contract exists but not yet confirmed (includes careGiverValidationStatus === 'pending')
 * - 'confirmed': careGiverValidationStatus === 'success' or 'rejected'
 *
 * @param nodeId - The booking contract UUID
 * @param accountKind - 'SITTER' for provider, 'PARENT' for seeker, or undefined for default (parent)
 * @param status - 'accepted' | 'confirmed'
 * @returns The generated URL path
 */
export const getBookingUrl = (nodeId: string, accountKind?: string, status?: 'accepted' | 'confirmed'): string => {
  return getContractUrl(nodeId, accountKind, 'booking', status);
};

/**
 * Generates a URL for an interview (contract or request).
 *
 * Note: A pending interview is a request, not a contract. Once accepted, it becomes a contract
 * (interviewKind "interview"). This function handles both cases for URL generation.
 *
 * @param nodeId - The interview UUID (contract or request)
 * @param accountKind - 'SITTER' for provider, 'PARENT' for seeker, or undefined for default (parent)
 * @param status - 'pending' | 'accepted'
 *   - 'pending': interview request (not yet a contract)
 *   - 'accepted': interview contract (interviewKind "interview")
 * @returns The generated URL path
 */
export const getInterviewUrl = (nodeId: string, accountKind?: string, status?: 'pending' | 'accepted'): string => {
  return getContractUrl(nodeId, accountKind, 'interview', status);
};

export const getRequestUrl = (nodeId: string): string => {
  return `/requests/${nodeId}`;
};

/**
 * Generates a URL for a contract node by automatically determining its type and status.
 * This is a convenience function that encapsulates the logic for determining contract type
 * (booking vs interview) and status (accepted vs confirmed).
 *
 * @param contract - The contract node object
 * @param accountKind - 'SITTER' for provider, 'PARENT' for seeker, or undefined for default (parent)
 * @returns The generated URL path
 */
export const getContractUrlFromNode = (contract: any, accountKind?: string): string => {
  if (!contract?.id) return '';

  const isBookingNode = isBooking(contract);
  const contractType = isBookingNode ? 'booking' : 'interview';

  let status: 'accepted' | 'confirmed';
  if (isBookingNode) {
    const confirmed = isConfirmed(contract);
    status = confirmed ? 'confirmed' : 'accepted';
  } else {
    status = 'accepted';
  }

  return getContractUrl(contract.id, accountKind, contractType, status);
};

/**
 * Generates a URL for a pending request node (booking or interview) by automatically determining its type.
 * This is a convenience function that encapsulates the logic for determining request type
 * (booking vs interview) and generates the appropriate pending URL.
 *
 * @param request - The request node object
 * @param accountKind - 'SITTER' for provider, 'PARENT' for seeker, or undefined for default (parent)
 * @returns The generated URL path for the pending request
 */
export const getRequestUrlFromNode = (request: any, accountKind?: string): string => {
  if (!request?.id) return '';

  const isBookingNode = isBooking(request);
  const contractType = isBookingNode ? 'booking' : 'interview';

  return getContractUrl(request.id, accountKind, contractType, 'pending');
};

export const getSitterJobsURL = (providerType: string): string => {
  const jobsURL = '/provider/jobs/search';

  switch (providerType) {
    case 'BABYSITTER':
      return `${jobsURL}?careType=babysitter`;
    case 'NANNY':
      return `${jobsURL}?careType=nanny`;
    case 'SPECIAL_NEEDS':
      return `${jobsURL}?careType=special_needs`;
    case 'COMPANION_CARE':
      return `${jobsURL}?careType=companion_care`;
    case 'PET_CARE':
      return `${jobsURL}?careType=pet_care`;
    case 'HOUSEKEEPING':
      return `${jobsURL}?careType=housekeeping`;
    default:
      return jobsURL;
  }
};

export const getParentJobsURL = (careType: string, zipcode: string): string => {
  const newSearchURL = '/availability/search';
  const oldSearchURL = '/profile/provider/search?search_strategy=';
  const zipcodeParam = zipcode ? `&zipcode=${zipcode}` : '';

  switch (careType) {
    case 'BABYSITTER':
      return `${newSearchURL}?careType=babysitter${zipcodeParam}`;
    case 'NANNY':
      return `${newSearchURL}?careType=nanny${zipcodeParam}`;
    case 'SPECIAL_NEEDS':
      return `${oldSearchURL}special_needs${zipcodeParam}`;
    case 'COMPANION_CARE':
      return `${oldSearchURL}companion_care${zipcodeParam}`;
    case 'PET_CARE':
      return `${oldSearchURL}pet_care${zipcodeParam}`;
    case 'HOUSEKEEPING':
      return `${oldSearchURL}housekeeping${zipcodeParam}`;
    default:
      return newSearchURL;
  }
};

/**
 * Decodes a notification message from a URL query parameter.
 * Vue Router typically decodes query parameters automatically, but this helper ensures
 * notifications are always human-readable, especially when they come from window.location.href
 * redirects or other sources that may not be automatically decoded.
 *
 * @param notification - The notification string from the URL query parameter (may be URL-encoded)
 * @returns The decoded, human-readable notification message, or null if notification is empty
 *
 * @example
 * decodeNotification('Interview%20request%20not%20found') // Returns: "Interview request not found"
 * decodeNotification('Interview request not found') // Returns: "Interview request not found" (unchanged)
 * decodeNotification(['Test%20message']) // Returns: "Test message"
 * decodeNotification(null) // Returns: null
 */
export const decodeNotification = (notification: string | string[] | null | undefined): string | null => {
  if (!notification) {
    return null;
  }

  const message = Array.isArray(notification) ? notification[0] : notification;
  if (!message || typeof message !== 'string') {
    return null;
  }

  try {
    // decodeURIComponent handles URL-encoded strings like "Interview%20request%20not%20found"
    // If the string is already decoded, it will return it unchanged (unless it contains invalid sequences)
    return decodeURIComponent(message);
  } catch (e) {
    // If decoding fails (e.g., already decoded or invalid encoding), return as-is
    // This handles edge cases where the notification might already be decoded
    return message;
  }
};

/**
 * Redirects to the bookings page with a notification message.
 * This helper encapsulates the common pattern of creating a URL with a notification
 * and performing a router redirect, used across pending booking/interview components.
 *
 * @param router - The Vue Router instance (typically `this.$router`)
 * @param message - The notification message to display
 * @param accountKind - 'SITTER' for provider, 'PARENT' for seeker, or undefined for default (parent)
 *
 * @example
 * redirectToBookingsWithNotification(this.$router, 'Request not found', 'PARENT');
 */
export const redirectToBookingsWithNotification = (router: any, message: string, accountKind?: string): void => {
  const url = getBookingsUrlWithNotification(message, accountKind);
  router.replace(url);
};
